import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState } from '../../utils/listPageState'
import { renderDateTime } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'

const DONATION_EXCHANGE_TABLE_COLUMNS = [
    { width: 70 },
    { width: 280 },
    { width: 130 },
    { width: 120 },
    { width: 140 },
    { width: 260 },
    { width: 110 },
    { width: 170 },
]

const DONATION_EXCHANGE_TABLE_MIN_WIDTH = DONATION_EXCHANGE_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const NUMERIC_CELL_SX = {
    whiteSpace: 'nowrap',
}

const EMAIL_CELL_SX = {
    wordBreak: 'break-all',
}

const formatUsedPoint = (usedPoint) => `${formatNumber(usedPoint)}P`

export default function DonationExchanges() {
    const location = useLocation()
    const history = useHistory()
    const initialListState = createInitialListState({ location })
    const [loading, setLoading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText || '')
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery || '')
    const [listErrorMessage, setListErrorMessage] = useState('')

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/donation/getExchangeList', {
                page: curPage,
                offset,
                searchText: searchQuery,
                searchSelect: {}
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '포인트 기부 내역을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '포인트 기부 내역을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, offset, searchQuery])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
        })

        if (location.search === nextSearch) {
            return
        }

        history.replace({
            pathname: location.pathname,
            search: nextSearch,
            ...(location.state ? { state: location.state } : {}),
        })
    }, [curPage, history, location.pathname, location.search, location.state, searchQuery])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 검색어를 실제 조회 조건으로 확정하고 첫 페이지부터 다시 본다.
    const searchButtonClick = () => {
        if (loading) {
            return
        }

        if (curPage === 1 && searchQuery === searchText) {
            getData()
            return
        }

        setSearchQuery(searchText)

        if (curPage !== 1) {
            setCurPage(1)
        }
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        const isInitialState = curPage === 1 && !searchText && !searchQuery

        if (isInitialState) {
            getData()
            return
        }

        setSearchText('')
        setSearchQuery('')
        setCurPage(1)
    }

    const hasActiveFilters = Boolean((searchQuery || '').trim())

    // 기부 내역의 조회 실패와 검색 결과 없음을 운영자가 구분할 수 있게 안내한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="포인트 기부 내역을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 포인트 기부 내역이 없습니다."
                    description="유저ID/상품명/이름/닉네임/이메일 검색어를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={loading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 포인트 기부 내역이 없습니다."
                description="포인트 기부 내역이 발생하면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Title>포인트 기부 내역</Title>
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={loading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="유저ID/상품명/이름/닉네임/이메일"
                                type="search"
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={loading}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={loading}><SearchIcon /></IconButton>
                        </ListFilterForm>

                    </ListFilterBar>

                    <ListTableContainer>
                        <Table size="small" sx={{ minWidth: DONATION_EXCHANGE_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                            <colgroup>
                                {DONATION_EXCHANGE_TABLE_COLUMNS.map((column, index) => (
                                    <col key={index} style={{ width: column.width }} />
                                ))}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>상품명</TableCell>
                                    <TableCell align="right" sx={NUMERIC_CELL_SX}>사용포인트</TableCell>
                                    <TableCell>이름</TableCell>
                                    <TableCell>닉네임</TableCell>
                                    <TableCell>이메일</TableCell>
                                    <TableCell>기기타입</TableCell>
                                    <TableCell>등록일</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {params.length > 0 ? (
                                    params.map((row) => (
                                        <TableRow key={row.donationExchangeId} hover>
                                            <TableCell>{row.rownum}</TableCell>
                                            <TableCell className="listTableLimitText">{row.snapshotTitle || row.donation_product?.title || '-'}</TableCell>
                                            <TableCell align="right" sx={NUMERIC_CELL_SX}>{formatUsedPoint(row.usedPoint)}</TableCell>
                                            <TableCell>{row.user?.username || '-'}</TableCell>
                                            <TableCell>{row.user?.nickName || '-'}</TableCell>
                                            <TableCell sx={EMAIL_CELL_SX}>{row.user?.email || '-'}</TableCell>
                                            <TableCell>{row.user?.deviceType || '-'}</TableCell>
                                            <TableCell>{renderDateTime(row.regTime)}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
